import MUIAvatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import { InsurancePolicyIcon } from "../icons/filled";
import ChangePhoto from "./change-photo.component";
import ExpandAndChangePhoto from "./expand-and-change-photo.component";

interface FilePickerProps {
  imgSrc: string;
  sx?: SxProps;
  onChangePhoto?: () => void;
  onExpandPhoto?: () => void;
  fileName?: string;
  imgWidth?: number;
  imgHeight?: number;
  fill?: boolean;
  /**
   * Opt in to `"contain"`. Defaults to `"cover"` (today's crop) because
   * insurance-card/ID surfaces may rely on the current crop — only the wide
   * practice/clinic logo previews, which must not be cropped, pass "contain".
   */
  objectFit?: "cover" | "contain";
}

export default function FilePicker({
  fileName,
  imgSrc,
  sx,
  onChangePhoto,
  onExpandPhoto,
  imgWidth = 250,
  imgHeight = 125,
  fill = false,
  objectFit = "cover"
}: FilePickerProps) {
  const { palette } = useTheme();

  const avatarSize = fill
    ? {
        width: "100%",
        height: "auto",
        aspectRatio: `${imgWidth} / ${imgHeight}`
      }
    : { width: imgWidth, height: imgHeight };
  const overlaySize = fill
    ? { width: "100%", height: "100%", left: 0 }
    : { width: imgWidth, height: imgHeight };

  return (
    <Box
      sx={{
        position: "relative",
        ...(fill ? { width: "100%" } : {}),
        ...sx
      }}
    >
      <MUIAvatar
        alt={fileName}
        src={imgSrc}
        variant="square"
        sx={avatarSize}
        imgProps={{ style: { objectFit } }}
      >
        <InsurancePolicyIcon />
      </MUIAvatar>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          ...overlaySize,
          backgroundColor: palette.avatar.bgcolor,
          opacity: 0,
          "&:hover": {
            opacity: 1
          }
        }}
      >
        {onChangePhoto && onExpandPhoto ? (
          <ExpandAndChangePhoto
            onExpandPhoto={onExpandPhoto}
            onChangePhoto={onChangePhoto}
          />
        ) : (
          <ChangePhoto onChangePhoto={onChangePhoto} />
        )}
      </Box>
    </Box>
  );
}
